import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { isMentor } from '../../util/data';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import {
  Avatar,
  InlineTextButton,
  Logo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  ExternalLink,
} from '../../components';
import { TopbarSearchForm } from '../../forms';

import MenuIcon from '../Topbar/MenuIcon';

import css from './TopbarDesktop.module.css';

const TopbarDesktop = props => {
  const {
    className,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    isMenteePage = false
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="DashboardPage"
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const yourListingsMenuItem = isMentor(currentUser) ? (
    <MenuItem key="ManageListingsPage">
      <NamedLink
        className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
        name="ManageListingsPage"
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.yourListingsLink" />
      </NamedLink>
    </MenuItem>
  ) : null;

  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {yourListingsMenuItem}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const findAMentorLink = !isMenteePage && (
    <NamedLink name="SearchPage" className={css.findAMentorLink}>
      <span className={css.findAMentor}>
        <FormattedMessage id="TopbarDesktop.findAMentor" />
      </span>
    </NamedLink>
  );

  const aboutLink = (
    <NamedLink name="AboutPage" className={css.aboutLink}>
      <span className={css.about}>
        <FormattedMessage id="TopbarDesktop.about" />
      </span>
    </NamedLink>
  );

  const howItWorksLink = (
    <NamedLink name="HowItWorksPage" className={css.howItWorksLink}>
      <span className={css.howItWorks}>
        <FormattedMessage id="TopbarDesktop.howItWorks" />
      </span>
    </NamedLink>
  );

  const faqLink = (
    <NamedLink name="FaqPage" className={css.faqLink}>
      <span className={css.faq}>
        <FormattedMessage id="TopbarDesktop.faq" />
      </span>
    </NamedLink>
  );

  const resourcesLink = (
    <ExternalLink href="https://careernavig8r.com/blog/" className={css.resourcesLink} sameTab={true}>
      <span className={css.resources}>
        <FormattedMessage id="TopbarDesktop.resources" />
      </span>
    </ExternalLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink
      name="SignupPage"
      className={css.signupLink}
      to={{ search: 'userType=mentor' }}
    >
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.beAMentor" />
      </span>
    </NamedLink>
  );

  const desktopMenu = (
    <Menu className={css.desktopMenu}>
      <MenuLabel className={css.desktopMenuLabel} isOpenClassName={css.desktopMenuIsOpen}>
        <MenuIcon className={css.menuIcon} />
        <FormattedMessage id="TopbarDesktop.menu" />
      </MenuLabel>
      <MenuContent className={css.desktopMenuContent}>
        <MenuItem key="SearchPage">
          {!isMenteePage && (
            <NamedLink
              className={classNames(css.desktopMenuLink, currentPageClass('SearchPage'))}
              name="SearchPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.findAMentor" />
            </NamedLink>
          )}
        </MenuItem>
        <MenuItem key="AboutPage">
          <NamedLink
            className={classNames(css.desktopMenuLink, currentPageClass('AboutPage'))}
            name="AboutPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.about" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="HowItWorksPage">
          <NamedLink
            className={classNames(css.desktopMenuLink, currentPageClass('HowItWorksPage'))}
            name="HowItWorksPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.howItWorks" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="FaqPage">
          <NamedLink
            className={classNames(css.desktopMenuLink, currentPageClass('FaqPage'))}
            name="FaqPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.faq" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="Blog">
          <ExternalLink href="https://careernavig8r.com/blog/" className={css.desktopMenuLink} sameTab={true}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.resources" />
          </ExternalLink>
        </MenuItem>
      </MenuContent>
    </Menu>
  )

  return (
    <nav className={classes}>
      <NamedLink className={css.logoLink} name="LandingPage">
        <Logo
          format="desktop"
          className={css.logo}
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
        />
      </NamedLink>
      {findAMentorLink}
      {aboutLink}
      {howItWorksLink}
      {faqLink}
      {resourcesLink}
      {desktopMenu}
      {inboxLink}
      {profileMenu}
      {!isAuthenticatedOrJustHydrated && (
        <div className={css.authLinks} >
          {loginLink}
          {signupLink}
        </div>
      )}
    </nav>
  );
};

const { bool, func, object, number, string } = PropTypes;

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
};

export default TopbarDesktop;
